import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Lightbulb,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  Question,
  Warning,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import TopBar from "../components/TopBar";
import { fetchIdeas, transitionIdea } from "../lib/ideasApi";

/**
 * IdeasInboxPage — [C86] Phase 3 Owner triage UI for cross-product idea
 * submissions (per ADR-MP-013). Two-column master-detail consistent with
 * RoadmapPage. Left list of intake rows; right detail with state-transition
 * actions.
 *
 * Authority: ADR-MP-013 + shared-infra-public/cross-product-mp-roadmap-ideas-integration-guide.md v1.1.
 * Tracker:   OPEN_PROMISES.md [C86] Phase 3 (this commit).
 */

const STATE_FILTERS = [
  { key: "all_open", label: "Open (untriaged + needs-info)", value: "untriaged,needs-info:target-app-assignment,needs-info:body-clarification" },
  { key: "untriaged", label: "Untriaged only", value: "untriaged" },
  { key: "needs_info", label: "Needs info", value: "needs-info:target-app-assignment,needs-info:body-clarification" },
  { key: "accepted", label: "Accepted (pending ratification)", value: "accepted-pending-ratification" },
  { key: "ratified", label: "Ratified", value: "ratified-as-Cn" },
  { key: "closed", label: "Rejected + deferred", value: "rejected,deferred" },
  { key: "all", label: "All states", value: "" },
];

const STATE_PRESENTATION = {
  "untriaged": { label: "Untriaged", color: "hsl(var(--status-warning))", icon: Clock },
  "accepted-pending-ratification": { label: "Accepted (pending Cn)", color: "hsl(var(--primary))", icon: CheckCircle },
  "ratified-as-Cn": { label: "Ratified", color: "hsl(var(--status-success))", icon: CheckCircle },
  "rejected": { label: "Rejected", color: "hsl(var(--status-critical))", icon: XCircle },
  "deferred": { label: "Deferred", color: "hsl(var(--muted-foreground))", icon: Clock },
  "needs-info:target-app-assignment": { label: "Needs target-app", color: "hsl(var(--status-warning))", icon: Question },
  "needs-info:body-clarification": { label: "Needs clarification", color: "hsl(var(--status-warning))", icon: Question },
};

function formatLocal(iso) {
  // Display-layer timezone canon per ADR-MP-014 (cycle-5c+25-fix52).
  // Converts UTC ISO timestamp from backend to viewer's local TZ with visible short label.
  // Reference format: "Jun 17 14:32 AEST" (compact month + day + 24hr time + short TZ label).
  // Detection uses browser-native Intl.DateTimeFormat (Owner pick 1a) — NOT IP-geo.
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZoneName: "short",
    }).format(d);
  } catch {
    return iso;
  }
}

export default function IdeasInboxPage() {
  const { isAuthenticated, getIdTokenClaims } = useAuth0();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const selectedUuid = searchParams.get("uuid") || null;
  const filterKey = searchParams.get("filter") || "all_open";
  const filterDef = STATE_FILTERS.find((f) => f.key === filterKey) || STATE_FILTERS[0];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [narrowView, setNarrowView] = useState(selectedUuid ? "detail" : "list");

  const getToken = useCallback(async () => {
    const claims = await getIdTokenClaims();
    if (!claims?.__raw) throw new Error("Not signed in.");
    return claims.__raw;
  }, [getIdTokenClaims]);

  const loadRows = useCallback(async () => {
    if (!isAuthenticated) return;
    setRefreshing(true);
    setError(null);
    try {
      const token = await getToken();
      const data = await fetchIdeas({ state: filterDef.value || undefined, token });
      setRows(data || []);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Failed to load ideas";
      setError(detail);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isAuthenticated, filterDef.value, getToken]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const selectedRow = useMemo(
    () => rows.find((r) => r.intake_uuid === selectedUuid) || null,
    [rows, selectedUuid],
  );

  const handleSelect = useCallback(
    (uuid) => {
      if (uuid) {
        const next = new URLSearchParams(searchParams);
        next.set("uuid", uuid);
        setSearchParams(next);
        setNarrowView("detail");
      } else {
        const next = new URLSearchParams(searchParams);
        next.delete("uuid");
        setSearchParams(next);
        setNarrowView("list");
      }
    },
    [searchParams, setSearchParams],
  );

  const handleFilterChange = useCallback(
    (newKey) => {
      const next = new URLSearchParams(searchParams);
      next.set("filter", newKey);
      next.delete("uuid");
      setSearchParams(next);
      setNarrowView("list");
    },
    [searchParams, setSearchParams],
  );

  const handleTransitioned = useCallback(
    async (updatedRow) => {
      setRows((prev) => {
        const idx = prev.findIndex((r) => r.intake_uuid === updatedRow.intake_uuid);
        if (idx < 0) return prev;
        const next = [...prev];
        next[idx] = updatedRow;
        return next;
      });
      // Refetch so a row that no longer matches the current filter drops out.
      await loadRows();
    },
    [loadRows],
  );

  const handleHome = useCallback(() => {
    navigate("/");
  }, [navigate]);

  return (
    <div className="min-h-screen" style={{ background: "hsl(var(--background))" }}>
      <TopBar onRefresh={loadRows} refreshing={refreshing} onHome={handleHome} />

      <main className="mx-auto max-w-[1600px] px-3 sm:px-4 lg:px-6 py-3 sm:py-6">
        <div className="flex items-center gap-3 mb-4">
          <Lightbulb size={20} style={{ color: "hsl(var(--primary))" }} />
          <h1
            className="text-xl font-semibold"
            style={{ color: "hsl(var(--foreground))" }}
            data-testid="ideas-inbox-heading"
          >
            Ideas Inbox
          </h1>
          <span
            className="text-mono ml-1"
            style={{ fontSize: "0.7rem", color: "hsl(var(--muted-foreground))" }}
          >
            — Cross-product · {rows.length} in current filter
          </span>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border px-2 sm:px-3 py-1.5 text-xs transition-colors hover:border-[hsl(var(--primary))]"
            style={{ borderColor: "hsl(var(--border))", color: "hsl(var(--foreground))" }}
            data-testid="ideas-back-to-roadmap"
          >
            <ArrowLeft size={12} />
            Back to Roadmap
          </button>
        </div>

        {error && (
          <div
            className="surface-card mb-4 p-4 flex items-start gap-3"
            style={{
              borderColor: "hsl(var(--status-critical) / 0.5)",
              background: "hsl(var(--status-critical) / 0.05)",
            }}
            data-testid="ideas-error-banner"
          >
            <Warning size={18} style={{ color: "hsl(var(--status-critical))" }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: "hsl(var(--status-critical))" }}>
                Failed to load ideas inbox
              </p>
              <p className="text-xs mt-1" style={{ color: "hsl(var(--muted-foreground))" }}>
                {error}
              </p>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-4" data-testid="ideas-filter-pills">
          {STATE_FILTERS.map((f) => {
            const active = f.key === filterKey;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => handleFilterChange(f.key)}
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2"
                style={{
                  borderColor: active ? "hsl(var(--primary))" : "hsl(var(--border))",
                  background: active ? "hsl(var(--primary) / 0.1)" : "transparent",
                  color: active ? "hsl(var(--primary))" : "hsl(var(--foreground))",
                  fontWeight: active ? 600 : 400,
                }}
                data-testid={`ideas-filter-${f.key}`}
              >
                {f.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={loadRows}
            disabled={refreshing}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors disabled:opacity-50"
            style={{ borderColor: "hsl(var(--border))", color: "hsl(var(--muted-foreground))" }}
            data-testid="ideas-refresh-btn"
          >
            <ArrowsClockwise size={11} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        <div className="lg:grid lg:grid-cols-[420px_1fr] lg:gap-6">
          {/* LIST column */}
          <div className={`${narrowView === "detail" ? "hidden lg:block" : "block"} mb-4 lg:mb-0`}>
            {loading ? (
              <div className="surface-card animate-pulse h-96" />
            ) : (
              <IdeasListPane rows={rows} selectedUuid={selectedUuid} onSelect={handleSelect} />
            )}
          </div>

          {/* DETAIL column */}
          <div className={`${narrowView === "list" && selectedUuid ? "hidden lg:block" : "block"}`}>
            {selectedRow && (
              <button
                type="button"
                onClick={() => setNarrowView("list")}
                className="inline-flex lg:hidden items-center gap-1.5 mb-3 rounded-md border px-3 py-1.5 text-sm hover:border-[hsl(var(--primary))]"
                style={{
                  borderColor: "hsl(var(--border))",
                  color: "hsl(var(--foreground))",
                  background: "hsl(var(--background))",
                }}
                data-testid="ideas-back-to-list"
              >
                <ArrowLeft size={13} />
                Back to list
              </button>
            )}
            {loading ? (
              <div className="surface-card animate-pulse h-96" />
            ) : selectedRow ? (
              <IdeasDetailPane row={selectedRow} getToken={getToken} onTransitioned={handleTransitioned} />
            ) : (
              <IdeasEmptyState rowCount={rows.length} />
            )}
          </div>
        </div>

        <footer
          className="mt-12 pt-6 border-t text-center"
          style={{ borderColor: "hsl(var(--border))" }}
        >
          <p
            className="text-mono"
            style={{ fontSize: "0.65rem", color: "hsl(var(--muted-foreground))" }}
          >
            [C86] Cross-product idea ingress · ADR-MP-013 · integration guide v1.1
          </p>
        </footer>
      </main>
    </div>
  );
}

// ── List pane ───────────────────────────────────────────────────────────────

function IdeasListPane({ rows, selectedUuid, onSelect }) {
  return (
    <div className="surface-card flex flex-col responsive-list-pane" data-testid="ideas-list-pane">
      <div
        className="flex items-center justify-between px-3 py-2 border-b"
        style={{ borderColor: "hsl(var(--border))" }}
      >
        <span
          className="text-mono"
          style={{
            fontSize: "0.65rem",
            color: "hsl(var(--muted-foreground))",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            fontWeight: 700,
          }}
        >
          {rows.length} {rows.length === 1 ? "idea" : "ideas"}
        </span>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-2" data-testid="ideas-list-scroll">
        {rows.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-xs" style={{ color: "hsl(var(--muted-foreground))" }}>
              No ideas in this filter.
            </p>
          </div>
        ) : (
          rows.map((row) => (
            <IdeasListRow
              key={row.intake_uuid}
              row={row}
              isSelected={row.intake_uuid === selectedUuid}
              onSelect={onSelect}
            />
          ))
        )}
      </div>
    </div>
  );
}

function IdeasListRow({ row, isSelected, onSelect }) {
  const pres = STATE_PRESENTATION[row.state] || {
    label: row.state,
    color: "hsl(var(--muted-foreground))",
    icon: Question,
  };
  const Icon = pres.icon;
  return (
    <button
      type="button"
      onClick={() => onSelect(row.intake_uuid)}
      className="w-full text-left rounded-md border p-3 transition-colors focus-visible:outline-none focus-visible:ring-2"
      style={{
        borderColor: isSelected ? "hsl(var(--primary))" : "hsl(var(--border))",
        background: isSelected ? "hsl(var(--primary) / 0.05)" : "hsl(var(--background))",
      }}
      data-testid={`ideas-list-row-${row.intake_uuid}`}
    >
      <div className="flex items-start gap-2">
        <Icon size={14} style={{ color: pres.color, marginTop: 2, flexShrink: 0 }} />
        <div className="flex-1 min-w-0">
          <div
            className="text-sm font-medium truncate"
            style={{ color: "hsl(var(--foreground))" }}
          >
            {row.title}
          </div>
          <div
            className="text-xs mt-1 line-clamp-2"
            style={{ color: "hsl(var(--muted-foreground))" }}
          >
            {row.body}
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span
              className="text-mono px-1.5 py-0.5 rounded"
              style={{
                fontSize: "0.6rem",
                background: "hsl(var(--muted))",
                color: "hsl(var(--foreground))",
              }}
              data-testid={`ideas-row-source-${row.intake_uuid}`}
            >
              {row.source}
            </span>
            {row.target_app_resolved && (
              <span
                className="text-mono px-1.5 py-0.5 rounded"
                style={{
                  fontSize: "0.6rem",
                  background: "hsl(var(--primary) / 0.1)",
                  color: "hsl(var(--primary))",
                }}
              >
                {row.target_app_resolved}
              </span>
            )}
            <span
              className="text-mono px-1.5 py-0.5 rounded"
              style={{
                fontSize: "0.6rem",
                background: `${pres.color} / 0.1`,
                color: pres.color,
                border: `1px solid ${pres.color}`,
              }}
            >
              {pres.label}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}

function IdeasEmptyState({ rowCount }) {
  return (
    <div
      className="surface-card p-8 text-center"
      style={{ borderColor: "hsl(var(--border))" }}
      data-testid="ideas-empty-state"
    >
      <Lightbulb size={40} style={{ color: "hsl(var(--muted-foreground))", margin: "0 auto" }} />
      <p className="text-sm mt-4" style={{ color: "hsl(var(--foreground))" }}>
        {rowCount > 0 ? "Select an idea to triage." : "No cross-product ideas in this filter yet."}
      </p>
      <p className="text-xs mt-2" style={{ color: "hsl(var(--muted-foreground))" }}>
        Ideas arrive from sending agents (currently: IK) via the bearer-gated `POST /api/mp/ideas` endpoint per ADR-MP-013.
      </p>
    </div>
  );
}

// ── Detail pane ─────────────────────────────────────────────────────────────

function IdeasDetailPane({ row, getToken, onTransitioned }) {
  const pres = STATE_PRESENTATION[row.state] || {
    label: row.state,
    color: "hsl(var(--muted-foreground))",
    icon: Question,
  };
  const Icon = pres.icon;
  const [action, setAction] = useState(null); // 'ratify' | 'reject' | 'defer' | 'reassign' | 'request_info'
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState(null);

  // form state for actions
  const [note, setNote] = useState("");
  const [ratifiedAs, setRatifiedAs] = useState("");
  const [rejectedReason, setRejectedReason] = useState("");
  const [deferredCondition, setDeferredCondition] = useState("");
  const [newTargetApp, setNewTargetApp] = useState("");

  const resetForm = () => {
    setAction(null);
    setNote("");
    setRatifiedAs("");
    setRejectedReason("");
    setDeferredCondition("");
    setNewTargetApp("");
    setActionError(null);
  };

  const submit = async () => {
    setSubmitting(true);
    setActionError(null);
    try {
      const token = await getToken();
      let payload = { note: note.trim() || null };
      if (action === "ratify") {
        if (!ratifiedAs.trim()) {
          throw new Error("Cn reference is required (e.g. C87).");
        }
        payload = { ...payload, target_state: "ratified-as-Cn", ratified_as: ratifiedAs.trim() };
      } else if (action === "reject") {
        payload = { ...payload, target_state: "rejected", rejected_reason: rejectedReason.trim() || null };
      } else if (action === "return_to_ik") {
        // [C86] Phase-6-adjacent operational extension: IK auto-send recovery loop.
        // Per MP→IK letter 2026-06-12 (cycle-5c+25-fix49): rejecting an intake with
        // the canonical reject_reason `"return-to-source"` signals IK to reopen the
        // originating task and surface it in IK's Right-now feed with a banner
        // "Returned from MP". The state transition is identical to a normal reject
        // (target_state="rejected") — the only difference is the reserved
        // rejected_reason code. Owner can append additional context via the note
        // field; the canonical code stays "return-to-source" (free-form Owner text
        // would NOT be picked up by IK's polling-loop matcher).
        payload = { ...payload, target_state: "rejected", rejected_reason: "return-to-source" };
      } else if (action === "defer") {
        payload = { ...payload, target_state: "deferred", deferred_condition: deferredCondition.trim() || null };
      } else if (action === "reassign") {
        if (!newTargetApp.trim()) {
          throw new Error("New target-app code is required.");
        }
        payload = { ...payload, target_state: "untriaged", new_target_app: newTargetApp.trim() };
      } else if (action === "request_info") {
        payload = { ...payload, target_state: "needs-info:body-clarification" };
      } else if (action === "accept") {
        payload = { ...payload, target_state: "accepted-pending-ratification" };
      }
      const updated = await transitionIdea(row.intake_uuid, payload, token);
      await onTransitioned(updated);
      resetForm();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Transition failed";
      setActionError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="surface-card p-4 sm:p-6" data-testid="ideas-detail-pane">
      <div className="flex items-start gap-2 mb-3">
        <Icon size={18} style={{ color: pres.color, marginTop: 4 }} />
        <div className="flex-1">
          <h2
            className="text-lg font-semibold"
            style={{ color: "hsl(var(--foreground))" }}
            data-testid="ideas-detail-title"
          >
            {row.title}
          </h2>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span
              className="text-mono px-2 py-0.5 rounded"
              style={{
                fontSize: "0.65rem",
                color: pres.color,
                border: `1px solid ${pres.color}`,
              }}
              data-testid="ideas-detail-state-badge"
            >
              {pres.label}
            </span>
            <span
              className="text-mono"
              style={{ fontSize: "0.65rem", color: "hsl(var(--muted-foreground))" }}
            >
              {row.intake_uuid}
            </span>
          </div>
        </div>
      </div>

      <div
        className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-4 p-3 rounded-md"
        style={{ background: "hsl(var(--muted) / 0.5)" }}
      >
        <MetaCell label="Source" value={row.source} />
        <MetaCell
          label="Target (received)"
          value={row.target_app_as_received || "—"}
        />
        <MetaCell
          label="Target (resolved)"
          value={row.target_app_resolved || "(needs Owner assignment)"}
          highlight={!row.target_app_resolved}
        />
        <MetaCell label="Received" value={formatLocal(row.received_at)} />
        <MetaCell label="Submitted (sender clock)" value={formatLocal(row.submitted_at)} />
        {row.ratified_as && <MetaCell label="Ratified as" value={row.ratified_as} highlight />}
        {row.rejected_reason && (
          <MetaCell label="Rejected reason" value={row.rejected_reason} />
        )}
        {row.deferred_condition && (
          <MetaCell label="Deferred until" value={row.deferred_condition} />
        )}
      </div>

      <section className="mb-5">
        <SectionLabel>Idea body</SectionLabel>
        <p
          className="text-sm whitespace-pre-wrap rounded-md border p-3"
          style={{
            color: "hsl(var(--foreground))",
            background: "hsl(var(--background))",
            borderColor: "hsl(var(--border))",
          }}
          data-testid="ideas-detail-body"
        >
          {row.body}
        </p>
      </section>

      <section className="mb-5">
        <SectionLabel>Owner triage actions</SectionLabel>
        {actionError && (
          <div
            className="text-xs mb-3 p-2 rounded-md"
            style={{
              color: "hsl(var(--status-critical))",
              background: "hsl(var(--status-critical) / 0.08)",
              border: "1px solid hsl(var(--status-critical) / 0.3)",
            }}
            data-testid="ideas-action-error"
          >
            {actionError}
          </div>
        )}
        {!action ? (
          <div className="flex flex-wrap gap-2">
            <ActionButton onClick={() => setAction("accept")} color="hsl(var(--primary))" testId="ideas-action-accept">
              Accept (pending Cn)
            </ActionButton>
            <ActionButton onClick={() => setAction("ratify")} color="hsl(var(--status-success))" testId="ideas-action-ratify">
              Ratify as [Cn]
            </ActionButton>
            <ActionButton onClick={() => setAction("reject")} color="hsl(var(--status-critical))" testId="ideas-action-reject">
              Reject
            </ActionButton>
            <ActionButton onClick={() => setAction("return_to_ik")} color="hsl(var(--status-warning))" testId="ideas-action-return-to-ik">
              Return to IK
            </ActionButton>
            <ActionButton onClick={() => setAction("defer")} color="hsl(var(--muted-foreground))" testId="ideas-action-defer">
              Defer
            </ActionButton>
            <ActionButton onClick={() => setAction("reassign")} color="hsl(var(--status-warning))" testId="ideas-action-reassign">
              Reassign target-app
            </ActionButton>
            <ActionButton onClick={() => setAction("request_info")} color="hsl(var(--status-warning))" testId="ideas-action-request-info">
              Request more info
            </ActionButton>
          </div>
        ) : (
          <div
            className="rounded-md border p-4"
            style={{ borderColor: "hsl(var(--border))" }}
            data-testid="ideas-action-form"
          >
            <ActionForm
              action={action}
              row={row}
              note={note}
              setNote={setNote}
              ratifiedAs={ratifiedAs}
              setRatifiedAs={setRatifiedAs}
              rejectedReason={rejectedReason}
              setRejectedReason={setRejectedReason}
              deferredCondition={deferredCondition}
              setDeferredCondition={setDeferredCondition}
              newTargetApp={newTargetApp}
              setNewTargetApp={setNewTargetApp}
            />
            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors disabled:opacity-50"
                style={{
                  background: "hsl(var(--primary))",
                  color: "hsl(var(--primary-foreground))",
                }}
                data-testid="ideas-action-submit"
              >
                {submitting ? "Submitting…" : "Confirm"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors"
                style={{
                  borderColor: "hsl(var(--border))",
                  color: "hsl(var(--foreground))",
                }}
                data-testid="ideas-action-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      <section>
        <SectionLabel>History</SectionLabel>
        <ol
          className="text-xs space-y-2 border-l-2 pl-3"
          style={{ borderColor: "hsl(var(--border))" }}
          data-testid="ideas-history-list"
        >
          {(row.history || []).map((h, idx) => (
            <li key={idx}>
              <span
                className="text-mono"
                style={{ fontSize: "0.65rem", color: "hsl(var(--muted-foreground))" }}
              >
                {formatLocal(h.at)}
              </span>
              <span
                className="ml-2"
                style={{ color: "hsl(var(--foreground))" }}
              >
                {h.transition}
              </span>
              {h.by && (
                <span
                  className="ml-2 text-mono"
                  style={{ fontSize: "0.65rem", color: "hsl(var(--muted-foreground))" }}
                >
                  by {h.by}
                </span>
              )}
              {h.note && (
                <div
                  className="mt-1 italic"
                  style={{ color: "hsl(var(--muted-foreground))" }}
                >
                  “{h.note}”
                </div>
              )}
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function MetaCell({ label, value, highlight }) {
  return (
    <div>
      <div
        className="text-mono"
        style={{
          fontSize: "0.6rem",
          color: "hsl(var(--muted-foreground))",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontWeight: 700,
        }}
      >
        {label}
      </div>
      <div
        className="text-sm mt-0.5"
        style={{
          color: highlight ? "hsl(var(--status-warning))" : "hsl(var(--foreground))",
          fontWeight: highlight ? 600 : 400,
          wordBreak: "break-word",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div
      className="text-mono mb-2"
      style={{
        fontSize: "0.65rem",
        color: "hsl(var(--muted-foreground))",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        fontWeight: 700,
      }}
    >
      {children}
    </div>
  );
}

function ActionButton({ children, onClick, color, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2"
      style={{
        borderColor: color,
        color: color,
        background: "transparent",
      }}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function ActionForm(props) {
  const {
    action,
    note,
    setNote,
    ratifiedAs,
    setRatifiedAs,
    rejectedReason,
    setRejectedReason,
    deferredCondition,
    setDeferredCondition,
    newTargetApp,
    setNewTargetApp,
  } = props;

  return (
    <div className="space-y-3">
      {action === "ratify" && (
        <FieldRow label="Cn reference (e.g. C87)" required>
          <input
            type="text"
            value={ratifiedAs}
            onChange={(e) => setRatifiedAs(e.target.value)}
            placeholder="C87"
            className="w-full rounded-md border px-2 py-1.5 text-sm"
            style={{
              borderColor: "hsl(var(--border))",
              background: "hsl(var(--background))",
              color: "hsl(var(--foreground))",
            }}
            data-testid="ideas-form-ratified-as"
          />
          <p className="text-xs mt-1" style={{ color: "hsl(var(--muted-foreground))" }}>
            Note: this records the Cn assignment but does NOT auto-author the row in `OPEN_PROMISES.md` — that stays a manual MP canon-write cycle per ADR-MP-013.
          </p>
        </FieldRow>
      )}
      {action === "reject" && (
        <FieldRow label="Rejection reason (optional)">
          <textarea
            value={rejectedReason}
            onChange={(e) => setRejectedReason(e.target.value)}
            rows={3}
            className="w-full rounded-md border px-2 py-1.5 text-sm"
            style={{
              borderColor: "hsl(var(--border))",
              background: "hsl(var(--background))",
              color: "hsl(var(--foreground))",
            }}
            data-testid="ideas-form-rejected-reason"
          />
        </FieldRow>
      )}
      {action === "defer" && (
        <FieldRow label="Defer condition / wait-for (optional)">
          <input
            type="text"
            value={deferredCondition}
            onChange={(e) => setDeferredCondition(e.target.value)}
            placeholder="e.g. after [C79] migration window closes"
            className="w-full rounded-md border px-2 py-1.5 text-sm"
            style={{
              borderColor: "hsl(var(--border))",
              background: "hsl(var(--background))",
              color: "hsl(var(--foreground))",
            }}
            data-testid="ideas-form-deferred-condition"
          />
        </FieldRow>
      )}
      {action === "reassign" && (
        <FieldRow label="New target-app code" required>
          <input
            type="text"
            value={newTargetApp}
            onChange={(e) => setNewTargetApp(e.target.value)}
            placeholder="NB | RI | QB | RoadmapMP | Auth0Tenant"
            className="w-full rounded-md border px-2 py-1.5 text-sm"
            style={{
              borderColor: "hsl(var(--border))",
              background: "hsl(var(--background))",
              color: "hsl(var(--foreground))",
            }}
            data-testid="ideas-form-new-target-app"
          />
        </FieldRow>
      )}
      {action === "request_info" && (
        <p className="text-xs" style={{ color: "hsl(var(--muted-foreground))" }}>
          Marks the row as needing more detail. Add the specific clarification request in the note below.
        </p>
      )}
      {action === "return_to_ik" && (
        <div
          className="rounded-md border p-3"
          style={{
            borderColor: "hsl(var(--status-warning) / 0.5)",
            background: "hsl(var(--status-warning) / 0.05)",
          }}
          data-testid="ideas-form-return-to-ik-note"
        >
          <p className="text-xs" style={{ color: "hsl(var(--foreground))", fontWeight: 600 }}>
            Return to IK — auto-send recovery loop
          </p>
          <p className="text-xs mt-1.5" style={{ color: "hsl(var(--muted-foreground))" }}>
            State transition: <span className="text-mono">untriaged → rejected</span> with reserved reject reason{" "}
            <span className="text-mono">&quot;return-to-source&quot;</span>. IK polls{" "}
            <span className="text-mono">/api/mp/ideas/{"{uuid}"}/status</span> and will detect this canonical
            code, reopen the originating IK task, and surface it in IK&apos;s Right-now feed with a banner asking Owner
            to re-categorise (task / journal / not-an-idea). Use this when an IK auto-send landed here as a false
            positive — i.e. the idea is real but doesn&apos;t belong on the roadmap. Use plain <strong>Reject</strong>{" "}
            instead when the item is not actionable anywhere (spam, accidental, etc.).
          </p>
          <p className="text-xs mt-1.5" style={{ color: "hsl(var(--muted-foreground))" }}>
            Optional Owner context can be added via the note field below; the reserved reject reason code itself is
            fixed.
          </p>
        </div>
      )}
      {action === "accept" && (
        <p className="text-xs" style={{ color: "hsl(var(--muted-foreground))" }}>
          Accepts the idea as-stated, pending later ratification as a [Cn] row.
        </p>
      )}
      <FieldRow label="Owner note (optional)">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="Any context for the history log…"
          className="w-full rounded-md border px-2 py-1.5 text-sm"
          style={{
            borderColor: "hsl(var(--border))",
            background: "hsl(var(--background))",
            color: "hsl(var(--foreground))",
          }}
          data-testid="ideas-form-note"
        />
      </FieldRow>
    </div>
  );
}

function FieldRow({ label, required, children }) {
  return (
    <div>
      <label
        className="text-mono block mb-1"
        style={{
          fontSize: "0.65rem",
          color: "hsl(var(--muted-foreground))",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontWeight: 700,
        }}
      >
        {label}
        {required && (
          <span style={{ color: "hsl(var(--status-critical))", marginLeft: 4 }}>*</span>
        )}
      </label>
      {children}
    </div>
  );
}
